import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { LOADING_STATE } from "src/types/constants";
import AutomatedJobsService from "src/services/automatedJobsService";
import {
  BatchJobDefinition,
  DataSource,
  ValidateFilterClauseResponse,
} from "src/services/automatedJobsService";
import { AutomatedJobFormData } from "src/components/pages/automatedJobs/components/automatedJobForm";

export type AutomatedBatchJobState = {
  automatedJobs: BatchJobDefinition[];
  getAutomatedJobsloading: LOADING_STATE;
  upsertBatchJobDefinitionLoading: LOADING_STATE;
  selectedJob: BatchJobDefinition | null;
  // Filter validation state
  filterValidationLoading: LOADING_STATE;
  filterValidationResult: ValidateFilterClauseResponse | null;
  // Data sources state
  dataSources: DataSource[];
  getDataSourcesLoading: LOADING_STATE;
};

const initialState: AutomatedBatchJobState = {
  automatedJobs: [],
  getAutomatedJobsloading: "idle",
  upsertBatchJobDefinitionLoading: "idle",
  selectedJob: null,
  // Filter validation initial state
  filterValidationLoading: "idle",
  filterValidationResult: null,
  // Data sources initial state
  dataSources: [],
  getDataSourcesLoading: "idle",
};

export const getAutomatedJobs = createAsyncThunk<BatchJobDefinition[], void>(
  "automatedBatchJob/fetchAutomatedJobs",
  async () => {
    const data = await AutomatedJobsService.getAutomatedJobs();
    return data;
  },
);

export const upsertBatchJobDefinition = createAsyncThunk<
  BatchJobDefinition,
  { jobData: AutomatedJobFormData; id?: string }
>("automatedBatchJob/upsertBatchJobDefinition", async ({ jobData, id }) => {
  if (id) {
    // Update existing job
    const data = await AutomatedJobsService.updateAutomatedJob(id, jobData);
    return data;
  } else {
    // Create new job
    const data = await AutomatedJobsService.createAutomatedJob(jobData);
    return data;
  }
});

export const getDataSources = createAsyncThunk<DataSource[], void>(
  "automatedBatchJob/fetchDataSources",
  async () => {
    const data = await AutomatedJobsService.getDataSources();
    return data;
  },
);

export const validateFilterClause = createAsyncThunk<
  ValidateFilterClauseResponse,
  { dataSource: DataSource; filterClause: string; jobId?: string }
>(
  "automatedBatchJob/validateFilterClause",
  async ({ dataSource, filterClause, jobId }, { rejectWithValue }) => {
    try {
      const data = await AutomatedJobsService.validateFilterClause(dataSource, filterClause, jobId);
      return data;
    } catch (error: any) {
      // Extract the error message properly
      const errorMessage = error?.response?.data?.detail || error?.message || "Validation failed";
      return rejectWithValue(errorMessage);
    }
  },
);

const automatedBatchJobSlice = createSlice({
  name: "automatedBatchJob",
  initialState,
  reducers: {
    setSelectedJob: (state, action) => {
      state.selectedJob = action.payload;
    },
    clearSelectedJob: (state) => {
      state.selectedJob = null;
    },
    clearFilterValidation: (state) => {
      state.filterValidationResult = null;
      state.filterValidationLoading = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAutomatedJobs.pending, (state) => {
        state.getAutomatedJobsloading = "pending";
      })
      .addCase(getAutomatedJobs.fulfilled, (state, action) => {
        state.automatedJobs = action.payload;
        state.getAutomatedJobsloading = "fulfilled";
      })
      .addCase(getAutomatedJobs.rejected, (state, action) => {
        state.getAutomatedJobsloading = "rejected";
      })
      .addCase(upsertBatchJobDefinition.pending, (state) => {
        state.upsertBatchJobDefinitionLoading = "pending";
      })
      .addCase(upsertBatchJobDefinition.fulfilled, (state, action) => {
        const existingIndex = state.automatedJobs.findIndex((job) => job.id === action.payload.id);
        if (existingIndex !== -1) {
          // Update existing job
          state.automatedJobs[existingIndex] = action.payload;
        } else {
          // Add new job
          state.automatedJobs.push(action.payload);
        }
        state.upsertBatchJobDefinitionLoading = "fulfilled";
        state.selectedJob = action.payload;
      })
      .addCase(upsertBatchJobDefinition.rejected, (state, action) => {
        state.upsertBatchJobDefinitionLoading = "rejected";
      })
      .addCase(getDataSources.pending, (state) => {
        state.getDataSourcesLoading = "pending";
      })
      .addCase(getDataSources.fulfilled, (state, action) => {
        state.dataSources = action.payload;
        state.getDataSourcesLoading = "fulfilled";
      })
      .addCase(getDataSources.rejected, (state, action) => {
        state.getDataSourcesLoading = "rejected";
      })
      .addCase(validateFilterClause.pending, (state) => {
        state.filterValidationLoading = "pending";
      })
      .addCase(validateFilterClause.fulfilled, (state, action) => {
        state.filterValidationLoading = "fulfilled";
        state.filterValidationResult = action.payload;
      })
      .addCase(validateFilterClause.rejected, (state, action) => {
        state.filterValidationLoading = "rejected";
        state.filterValidationResult = null;
      });
  },
});

export const { setSelectedJob, clearSelectedJob, clearFilterValidation } =
  automatedBatchJobSlice.actions;
export default automatedBatchJobSlice.reducer;
